// Debounced person typeahead for tagging outing participants. Selected people
// render as removable rows above the search box; `excludeIds` keeps the owner
// (and anyone else ineligible) out of the suggestions.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { PEOPLE_SEARCH, type PeopleSearchResult, type PersonSummary } from "../lib/queries";
import { useT } from "../lib/i18n";
import { TextInput } from "./ui";

export function PersonPicker({
  selected,
  onChange,
  excludeIds = [],
}: {
  selected: PersonSummary[];
  onChange: (next: PersonSummary[]) => void;
  excludeIds?: string[];
}) {
  const { t, labelizeT } = useT();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const suggestions = useQuery({
    queryKey: ["people-picker", debounced],
    enabled: debounced.length >= 2,
    queryFn: () => gqlRequest<PeopleSearchResult>(PEOPLE_SEARCH, { q: `%${debounced}%` }),
  });

  const hidden = new Set([...excludeIds, ...selected.map((p) => p.id)]);
  const visible = suggestions.data?.people.nodes.filter((p) => !hidden.has(p.id)) ?? [];

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <ul className="space-y-1.5">
          {selected.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between rounded-xl border border-primary/25 bg-primary/5 px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">
                  {p.displayName ?? t("common.unnamed")}
                </span>
                {p.activities && p.activities.length > 0 && (
                  <span className="block text-xs text-muted-foreground">{p.activities.map(labelizeT).join(", ")}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s.id !== p.id))}
                className="ml-2 shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                aria-label={t("picker.removePersonAria", { name: p.displayName ?? t("common.unnamed") })}
              >
                <X aria-hidden className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <TextInput
          value={query}
          placeholder={t("picker.searchPeople")}
          onChange={(e) => setQuery(e.target.value)}
        />
        {debounced.length >= 2 && suggestions.data && (
          <div className="absolute top-full z-10 mt-1.5 max-h-56 w-full divide-y divide-border overflow-y-auto rounded-xl border bg-popover text-popover-foreground shadow-panel">
            {visible.length === 0 && <p className="p-3 text-sm text-muted-foreground">{t("picker.noPeople")}</p>}
            {visible.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  if (!selected.some((s) => s.id === p.id)) onChange([...selected, p]);
                  setQuery("");
                  setDebounced("");
                }}
                className="block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <span className="block truncate font-medium text-foreground">
                  {p.displayName ?? t("common.unnamed")}
                </span>
                {p.activities && p.activities.length > 0 && (
                  <span className="text-xs text-muted-foreground">{p.activities.map(labelizeT).join(", ")}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
